// Vendor Libs
import {
    cloneDeep,
    find,
    forEach,
    get,
    map,
    includes,
    isArray,
    isBoolean,
    isFunction,
    isNumber,
    isString,
    pullAt,
    uniq,
} from 'lodash';
import XLSX from 'xlsx';

// App Libs
import formatters from '../app/formatters';

const EXCEL_CONTENT_TYPE = 'application/octet-stream';
const EXCEL_MAX_CELL_LENGTH = 32767; // Excel's maximum characters per cell

const isExportable = (value) => isString(value) || isNumber(value) || isBoolean(value);

const truncateText = (value) => {
    if (isString(value) && value.length > EXCEL_MAX_CELL_LENGTH) {
        return `${value.substring(0, EXCEL_MAX_CELL_LENGTH - 3)}...`;
    }
    return value;
};

const convertToTable = (data, options) => {
    const {blacklistKeys = [], priorityKeys = [], keyOrder = [], parentKeys = [], customKeys = [], childKey} = options;
    let rows = [];
    let updatedData = cloneDeep(data);

    if (childKey && data.length) {
        updatedData = data.reduce((result, row) => {
            const parentData = parentKeys.reduce((result, key) => {
                result[key] = getValue(row, key, options);

                return result;
            }, {});
            const childData = get(row, childKey, []);
            const items = (isArray(childData) ? childData : [childData]).map((item) => ({...item, ...parentData}));

            result.push(...items);
            return result;
        }, []);
    }

    if (updatedData.length) {
        const nonExportableIndexes = [];
        const paths = uniq([
            ...priorityKeys,
            ...(childKey ? parentKeys : []),
            ...keyOrder,
            ...Object.keys(updatedData[0]),
        ]);

        map(paths, (column, i) => {
            let exportable = false;

            if (!includes(blacklistKeys, column)) {
                forEach(updatedData, (row) => {
                    const cell = getValue(row, column, options);

                    if (cell !== undefined && isExportable(cell)) {
                        exportable = true;
                        return false;
                    }
                });
            }

            if (!exportable) {
                nonExportableIndexes.push(i);
            }
        });

        pullAt(paths, nonExportableIndexes);

        const titlePaths = childKey
            ? paths.map((key) => (parentKeys.includes(key) ? key : `${childKey}.${key}`))
            : paths;

        const updatedTitles = titlePaths.map((t) => {
            const customTitle = customKeys.find((c) => c.key === t);
            return customTitle && customTitle.formatter ? customTitle.formatter : t;
        });

        rows = [updatedTitles, ...map(updatedData, (row) => map(paths, (path) => getValue(row, path, options)))];
    }

    return rows;
};

const getValue = (row, key, {customKeys = []}) => {
    const customKey = find(customKeys, (customKey) => customKey.key === key);

    const value = customKey && customKey.formatter && isFunction(formatters[customKey.formatter])
        ? formatters[customKey.formatter](get(row, key), row)
        : get(row, key);

    return truncateText(value);
};

const convertToBuffer = (stream) => {
    const buf = new ArrayBuffer(stream.length);
    const view = new Uint8Array(buf);

    for (let i = 0; i < stream.length; i++) {
        view[i] = stream.charCodeAt(i) & 0xff;
    }

    return buf;
};

const generateExcelData = (data, options, author) => {
    try {
        const rows = convertToTable(data, options);

        const workbook = XLSX.utils.book_new();
        workbook.Props = {
            Author: author,
        };
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Exported Items');

        return XLSX.write(workbook, {bookType: 'xlsx', type: 'binary'});
    } catch (error) {
        throw new Error('Failed to generate Excel file. Data may contain text that is too long for Excel cells.');
    }
};

const downloadExcelFile = (data, options, fileName = 'export.xlsx', author = '') => {
    try {
        const excelData = generateExcelData(data, options, author);

        const el = document.createElement('a');
        const blob = new Blob([convertToBuffer(excelData)], {
            type: EXCEL_CONTENT_TYPE,
        });

        el.href = URL.createObjectURL(blob);
        el.setAttribute('download', fileName);
        document.body.appendChild(el);
        el.click();

        setTimeout(() => {
            URL.revokeObjectURL(blob);
            document.body.removeChild(el);
        }, 1);
    } catch (error) {
        alert(`Export failed: ${error.message}`);
    }
};

export default downloadExcelFile;
